/* eslint-disable @typescript-eslint/no-unused-vars */
import { Loader } from "./loaders/assets/loader";

import SpaceLoader from "./loaders/space/space.loader";
import Renderer from "./renderer/renderer";
import EventService from "./services/event/event.service";
import CameraService from "./services/camera/camera.service";
import ArtworkLoader, {
  type ArtworkLoadTelemetrySnapshot,
} from "./loaders/artwork/artwork.loader";
import PhysicsService from "./services/physics/physics.service"
import SoundService from "./services/sound/sound.service"
import { SpaceData } from "../../types/spaceData";
import { Artwork, ArtworkWrapper } from "../../types/artworksData";
import { Gallery } from "../../types/galleryData";
import { Utils } from "./utils/utils";
import type { VRSessionState, VRViewerMedia } from "./renderer/managers/vr.types";
import { createArtetraRuntimeSession, type ArtetraRuntimeGeneration, type ArtetraRuntimeSessionSnapshot } from "./runtime/artetraRuntimeSession";
import { getRuntimeConfig } from "../../utils/runtimeConfig";

import AppData from "./app.data";

const appData = new AppData()

export interface IMain3DManager {
  spaceLoaderService: SpaceLoader;
  artworkLoaderService: ArtworkLoader;
  eventService: EventService;
  cameraService: CameraService;
  rendererService: Renderer;
  listener: any;
  loaderService: Loader;
  DATA: Gallery;
  soundService: SoundService;

  init(canvas: HTMLCanvasElement, onClickButton: () => void): void;
  loadGallery(options?: {
    keepLoadingScreen?: boolean;
    onSceneReady?: () => void;
    startBackgroundSound?: boolean;
    onLoadError?: (error: unknown) => void;
    onArtworkTelemetry?: (snapshot: ArtworkLoadTelemetrySnapshot) => void;
  }): Promise<unknown>;

  saveCameraPosition(space: string): void;
  loadCameraPosition(space: string): void;

  dispose(): void;
  destroy(): void;
  setDioramaClickEvent(method): void;
  getSelectedArtwork(): any;
  changeMood(mood: any, local?: any): void;
  isVRPresenting(): boolean;
  getVRSessionState(): VRSessionState | null;
  endVRSession(): Promise<void>;
  openVRViewer(media: VRViewerMedia): Promise<void>;
  closeVRViewer(): void;
  setVRSessionEvent(method: (state: VRSessionState) => void): void;
  setVRFailureEvent(method: (reason: string) => void): void;
  getRuntimeSnapshot(): ArtetraRuntimeSessionSnapshot | null;
}

class Main3DManager implements IMain3DManager {

  spaceLoaderService: SpaceLoader;
  artworkLoaderService: ArtworkLoader;
  eventService: EventService;
  cameraService: CameraService;
  physicsService: PhysicsService;
  rendererService: Renderer;
  listener: any;
  loaderService: Loader;
  DATA: Gallery;
  soundService: SoundService;
  utils: Utils;
  private isDestroyed = false;
  private isDisposed = false;
  private runtimeSession: ReturnType<typeof createArtetraRuntimeSession> | null = null;
  private runtimeInitializationPromise: Promise<void> | null = null;

  public init = (canvas: HTMLCanvasElement) => {
    this.isDestroyed = false;
    this.isDisposed = false;
    this.runtimeSession = createArtetraRuntimeSession({
      loadRuntimeConfig: getRuntimeConfig,
      getQuality: () => {
        if (typeof window === "undefined") return undefined;
        return window.localStorage?.getItem("renderQuality");
      },
    });
    this.runtimeInitializationPromise = null;
    void this.initializeRuntimeSession();
    this.loaderService = new Loader();
    this.cameraService = new CameraService(canvas);

    this.physicsService = new PhysicsService();
    this.cameraService.addPhysics(this.physicsService);

    this.rendererService = new Renderer(canvas, this.cameraService, this.physicsService);

    this.soundService = new SoundService(this.loaderService, this.cameraService)
    void this.initializeRuntimeSession().then(() => {
      this.soundService?.setRuntimeMediaDelivery?.(
        this.runtimeSession?.getSnapshot().mediaDelivery || null,
      );
    });

    this.spaceLoaderService = new SpaceLoader(this.loaderService, this.rendererService, this.physicsService, this.cameraService);
    this.artworkLoaderService = new ArtworkLoader(this.loaderService, this.soundService);
    this.artworkLoaderService.setRuntimeControls({
      createAssetHandle: (kind, label) => this.runtimeSession?.createAssetHandle(kind, label),
      useProgressiveLoading: () => Boolean(
        this.runtimeSession?.getSnapshot().progressiveLoadingEnabled
      ),
      useOptimizedAssets: () => Boolean(
        this.runtimeSession?.getSnapshot().optimizedAssetsEnabled
      ),
      getMediaDelivery: () => this.runtimeSession?.getSnapshot().mediaDelivery || null,
    });

    this.utils = new Utils();

    this.eventService = new EventService(this.rendererService, canvas, this.cameraService, this.artworkLoaderService, this.soundService);

  };

  private async initializeRuntimeSession() {
    if (!this.runtimeSession) return;
    if (!this.runtimeInitializationPromise) {
      this.runtimeInitializationPromise = this.runtimeSession.initialize()
        .then(() => undefined)
        .catch((error) => {
          console.warn("Main3DManager: runtime session initialization failed", error);
        });
    }

    return this.runtimeInitializationPromise;
  }

  private beginRuntimeGeneration(label: string): ArtetraRuntimeGeneration | null {
    return this.runtimeSession?.beginGeneration(label) || null;
  }

  private isRuntimeGenerationCurrent(generation: ArtetraRuntimeGeneration | null) {
    return !generation || Boolean(this.runtimeSession?.isCurrentGeneration(generation));
  }

  private cleanupRuntimeSession(reason: string) {
    const runtimeSession = this.runtimeSession;
    if (!runtimeSession) return;

    runtimeSession.beginGeneration(reason);
    runtimeSession.cleanup().catch((error) => {
      console.warn("Main3DManager: runtime session cleanup failed", error);
    });
  }

  public loadGallery = async (options: {
    keepLoadingScreen?: boolean;
    onSceneReady?: () => void;
    startBackgroundSound?: boolean;
    onLoadError?: (error: unknown) => void;
    onArtworkTelemetry?: (snapshot: ArtworkLoadTelemetrySnapshot) => void;
  } = {}) => {
    if (this.rendererService === null) return
    void this.initializeRuntimeSession();
    const runtimeGeneration = this.beginRuntimeGeneration("loadGallery");
    this.artworkLoaderService?.setRuntimeControls({
      isCurrent: () => this.isRuntimeGenerationCurrent(runtimeGeneration),
      onTelemetry: (snapshot) => options.onArtworkTelemetry?.(snapshot),
    });
    const loadingText = document.getElementById("loading")
    let timeOut = setTimeout(() => { })
    this.rendererService.initRenderer();
    this.rendererService.vrManager.setRaycaster(this.eventService?.raycasterHandler);
    this.rendererService.vrManager.setSoundService(this.soundService);

    try {
      await this.loadSpace();
      if (!this.isRuntimeGenerationCurrent(runtimeGeneration)) return;
      this.utils.applyHighQualityHints();
      this.cameraService.setCameraTransforms();
      this.rendererService.startAnimationLoop();
      options.onSceneReady?.();
      clearTimeout(timeOut)
      if (!options.keepLoadingScreen) {
        timeOut = setTimeout(() => {
          if (loadingText) {
            loadingText.style.opacity = "0.0";
            loadingText.style.pointerEvents = "none";
          }
        }, 1000)
      }


      if (options.startBackgroundSound !== false) {
        await this.loadBackgroundSound();
        if (!this.isRuntimeGenerationCurrent(runtimeGeneration)) return;
      }
      return await this.loadArtworks();
    } catch (error) {
      console.error("Error loading gallery:", error);
      options.onLoadError?.(error);
    }


  };

  public async loadSpace() {
    return await this.spaceLoaderService.loadSpace()
  }


  public updateArtworksVisibility() {
    this.utils.handleProceduralSpaceVisibility(appData)
    this.rendererService.addToRenderQueue()
  }
  public async loadArtworks() {
    return await this.artworkLoaderService.loadArtworks({
      useOptimizedAssets: this.runtimeSession?.getSnapshot().optimizedAssetsEnabled === true,
      mediaDelivery: this.runtimeSession?.getSnapshot().mediaDelivery || null,
    })
  }

  /** Reload only the background sound (e.g. after sound change in gallery settings) */
  public async loadBackgroundSound() {
    return await this.soundService.loadBackgroundSound()
  }

  public async loadArtwork(slot: number, artworkWrapper: ArtworkWrapper | null, listing: boolean) {
    return await this.artworkLoaderService.listArtworkInSlot(slot, artworkWrapper, listing)
  }

  public removeArtworks() {
    return this.artworkLoaderService.removeAllArtwoks()
  }

  public removeArtwork(slot: number) {
    return this.artworkLoaderService.removeArtwork(slot)
  }

  public updateSound(artwork: Artwork) {
    return this.artworkLoaderService.updateSound(artwork)
  }

  public updateArtworkPedestal(slot: number, artworkWrapper: ArtworkWrapper | null, listing: boolean) {
    return this.artworkLoaderService.updateArtworkPedestal(slot, artworkWrapper, listing)
  }



  public setKeyAvailability(availability: boolean) {
    this.eventService.setKeyUpAvailability(availability)
  }

  public setViewerRotationEnabled(enabled: boolean) {
    this.eventService.setViewerRotationEnabled(enabled)
  }

  public selectAndMoveArtwork = () => {
    this.eventService.selectAndMoveArtwork()
  }

  public resetSlotTransforms = (slot: string) => {
    this.eventService.resetSlotTransforms(slot)
  }

  public getMovedArtworks = () => {
    return this.eventService.getMovedArtworks()
  }



  public saveCameraPosition() {
    this.cameraService.saveCameraPositions();
  }

  public loadCameraPosition() {
    this.cameraService.loadCameraPositions();
  }

  public setDioramaClickEvent(method: Function) {
    // sets the method to be called when clicked on a diorama - an artwork form MoM space
    this.eventService.setDioramaClickedMethod(method)
  }

  public setArtworkClickEvent(method: Function) {

    // sets the method to be called when clicked on an artwork from Hexa sapce

    this.eventService.setArtworkClickedMethod(method);
  }

  public setSpaceEditClickEvent(method: any) {

    // sets the method to be called when a procedrfual space is added or removed

    this.eventService.setSpaceEditClickEvent(method);
  }

  public getSelectedArtwork() {
    if (this.eventService) {
      return this.eventService.getSelectedArtwork();
    }
    return null;
  }

  public async changeMood(mood: any, local?: any) {
    await this.spaceLoaderService.changeMood(mood, local);
  }

  public isVRPresenting() {
    return this.rendererService?.isVRPresenting?.() || false;
  }

  public getVRSessionState() {
    return this.rendererService?.getVRSessionState?.() || null;
  }

  public endVRSession() {
    return this.rendererService?.endVRSession?.() || Promise.resolve();
  }

  public async openVRViewer(media: VRViewerMedia) {
    await this.rendererService?.openVRViewer?.(media);
  }

  public closeVRViewer() {
    this.rendererService?.closeVRViewer?.();
  }

  public setVRSessionEvent(method: (state: VRSessionState) => void) {
    this.rendererService?.setVRSessionEvent?.(method);
  }

  public setVRFailureEvent(method: (reason: string) => void) {
    this.rendererService?.setVRFailureEvent?.(method);
  }

  public getRuntimeSnapshot() {
    return this.runtimeSession?.getSnapshot() || null;
  }

  public dispose = () => {
    if (this.isDisposed || this.isDestroyed) return;
    this.isDisposed = true;

    this.cleanupRuntimeSession("dispose");
    this.spaceLoaderService?.dispose();
    this.artworkLoaderService?.dispose();
    this.cameraService?.dispose();
    this.rendererService?.killRenderer();
    this.physicsService?.dispose();
    this.soundService?.stopSounds();
    this.soundService?.dispose();
    this.loaderService?.dispose();
  };

  public destroy = () => {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    if (!this.isDisposed) {
      this.isDisposed = true;
      this.cleanupRuntimeSession("destroy");
      this.rendererService?.killRenderer();
      this.cameraService?.destroy();
      this.spaceLoaderService?.destroy();
      this.artworkLoaderService?.dispose();
      this.loaderService?.dispose();
      this.soundService?.dispose();
    }
    appData.clearData();
    this.spaceLoaderService = null;
    this.artworkLoaderService = null;
    this.eventService = null;
    this.cameraService = null;
    this.rendererService = null;
    this.physicsService = null;
    this.soundService = null;
    this.listener = null;
    this.loaderService = null;
    this.runtimeSession = null;
    this.runtimeInitializationPromise = null;
  };
}

export default Main3DManager;
